import { categoryLabel, getCustomCategories, seatRuleAllows } from "./constants.js";
// frontend/src/shared/pricing/carCategory.js
// PURE auto-detect car type category from model name, seats, and brand
// ZERO BROWSER DEPENDENCIES
//
// The backend consumes THIS file via the `sramika-pricing` file: dependency in
// backend/package.json, which symlinks to this directory. It is a near-duplicate of
// frontend/src/utils/carCategory.js, which cannot be shared because it imports
// apiRequest. Behavioural changes must be applied to BOTH; carCategory.test.js asserts
// the two agree, so drift fails a test rather than surfacing as a mispriced vehicle.

// ── Hardcoded Keyword Fallbacks ──────────────────────────────────────────────

/* Vehicles placed by name rather than by keyword.

   A pickup is not a cab and a two-seat roadster is not a saloon, but there is no tier
   for either and they are in the catalogue, so they need somewhere to sit rather than
   falling to the five-seat default. Four named vehicles, not a family a keyword should
   try to describe - "hilux" as a keyword would be one more thing to keep in step.

   These are DEFAULTS, the same as the keywords: an admin rule on the same model wins,
   and so does a pin on an individual car. */
export const BUILTIN_MODEL_TIERS = {
  "mahindra pik-up": "SUV_7_LUX",
  "toyota hilux": "SUV_7_LUX",
  "isuzu d-max": "SUV_7_LUX",
  "mg cyberster": "SEDAN_5_LUX",
};

/* Seat counts for vehicles whose names do not state one.

   Every tier above eight seats is decided by the count, not by a word, so a coach with
   no number in its name had nowhere to go and fell to the saloon default. A real coach
   still arrives at onboarding carrying its own count and that wins over this - these
   decide where the CATALOGUE entry sits, and what a car with no count falls back to.

   Figures are the common passenger configuration for each. They are visible and
   editable per model on the Model rules tab, so a wrong one is a correction rather
   than a deploy. */
export const BUILTIN_MODEL_SEATS = {
  "ashok leyland oyster": 25,      // midi bus
  "sml isuzu s7": 26,
  "sml isuzu executive lx": 28,
  "tata cityride": 32,             // city bus
  "tata starbus": 32,
  "ashok leyland sunshine": 32,    // school bus
  "ashok leyland falcon": 40,      // intercity coach
  "ashok leyland viking": 40,
  "bharat benz 917 coach": 40,
  "volvo 9400 coach": 45,
  "scania metrolink": 45,
};

export const HARDCODED_KEYWORDS = {
  // ── Seven and eight seaters ───────────────────────────────────────────────
  SUV_7_PREMIUM:      ["fortuner", "endeavour", "kodiaq", "pajero", "land cruiser", "gloster",
                       "alturas", "mu-x", "tiguan allspace", "captiva", "trailblazer", "santa fe"],
  // The Innova Crysta sits with the Hycross: the Vijayawada taxi association prices it
  // above the older Innova (Rs 2600 / 4000 against 2300 / 3400 for 4 / 8 hours).
  SUV_7_LUX:          ["hycross", "innova hycross", "innova crysta", "crysta", "crysta zx", "crysta touring", "carnival",
                       "invicto", "vellfire", "alphard",
                       // Luxury seven-seaters, by model. Their brand token lives on the
                       // saloon tier, so these have to be named to win here first.
                       "x7", "q7", "q8", "gls", "gle", "xc90", "cayenne", "lx 500", "lexus lx",
                       "range rover", "discovery", "ev9", "mg m9", "xev 9s"],
  SUV_7_COMFORT:      ["innova", "marazzo", "xuv700", "xuv 7xo", "safari",
                       "hector plus", "alcazar", "hexa", "tarun"],
  SUV_7_STD:          ["carens", "tavera", "xylo", "lodgy", "xuv500", "bolero", "scorpio n",
                       "emax 7", "e max 7", "sumo", "limo green", "vinfast mpv 7", "aria", "gurkha 5"],
  SUV_7_ECO:          ["ertiga", "xl6", "maruti xl6", "triber", "rumion",
                       // Seven and eight seat vans priced at the entry seven-seat tier.
                       "eeco", "omni", "tata magic", "magic express", "mobilio", "enjoy",
                       "go plus", "go+", "evalia", "quanto", "br-v", "venture"],

  // ── Five seat SUVs ────────────────────────────────────────────────────────
  SUV_5:              ["scorpio", "thar", "meridian", "compass", "harrier", "hector plus 5",
                       "grand vitara 5", "xuv300", "xuv400", "jimny", "hector",
                       "tucson", "s-cross", "outlander", "eclipse cross", "wrangler",
                       "grand cherokee", "tuv300", "duster",
                       // Luxury five-seat SUVs. Named for the same reason as above.
                       "x1", "x3", "x5", "q3", "q5", "glc", "macan", "evoque", "velar",
                       "discovery sport", "volvo xc", "xc40", "xc60", "lexus nx", "lexus ux",
                       "lexus rx", "countryman", "defender",
                       // Electric SUVs now common on the road.
                       "atto 3", "sealion", "zs ev", "kona electric", "ioniq 5", "ev6",
                       "be 6", "xev 9e", "sierra ev", "enyaq", "c5 aircross", "clavis",
                       "vf 6", "vf 7", "e vitara", "harrier ev", "sierra", "cr-v", "tiguan", "yeti", "gurkha"],
  MINI_SUV_5:         ["brezza", "nexon", "creta", "venue", "sonet", "ecosport", "urban cruiser",
                       "magnite", "kushaq", "seltos", "taigun", "astor", "kicks", "terrano",
                       "fronx", "punch", "elevate", "c3 aircross", "kiger", "3xo", "syros",
                       "curvv", "windsor", "victoris", "taisor", "hyryder", "grand vitara", "wr-v",
                       "kylaq", "basalt", "tekton", "captur", "gypsy"],

  // ── Saloons, three bands ──────────────────────────────────────────────────
  /* Luxury. The brand tokens are the point: every saloon these makers sell belongs
     here, and naming each model would go stale with the next facelift. Their SUVs
     are named on the tiers above, which are checked first, so a brand token here
     cannot take an X5 or a Q7 with it. "benz" and "volvo" are deliberately absent -
     Bharat Benz 917 Coach and Volvo 9400 Coach contain them. */
  SEDAN_5_LUX:        ["bmw", "audi", "mercedes", "lexus", "porsche", "jaguar", "maserati",
                       "bentley", "volvo s", "3 series", "5 series", "7 series",
                       "c-class", "e-class", "s-class", "a-class", "panamera", "taycan",
                       "camry", "superb", "accord", "seal", "es 300", "passat"],

  /* Mid-size. The saloons a customer thinks of as a step up from a Dzire without
     being a luxury car - and the band the platform had no home for, which is why
     Verna and City were sitting on the luxury tier. */
  SEDAN_5_PREMIUM:    ["verna", "honda city", "ciaz", "yaris", "rapid", "octavia", "elantra",
                       "slavia", "virtus", "vento", "corolla altis", "cruze", "civic",
                       "jetta", "linea", "sx4", "fiesta", "laura"],

  /* Entry. The workhorses of an airport run. */
  SEDAN_5_STD:        ["dzire", "swift dzire", "etios", "amaze", "xcent", "aspire", "tigor",
                       "aura", "zest", "logan", "verito", "indigo", "sunny", "scala",
                       "ameo", "manza", "accent", "ikon", "esteem", "aveo"],

  HATCHBACK_5:        ["alto", "wagoner", "wagon r", "wagonr", "wagnor", "i10", "i20", "swift",
                       "santro", "beat", "kwid", "celerio", "baleno", "polo", "glanza",
                       "tiago", "altroz", "ignis", "c3", "comet", "exter", "s-presso",
                       "ritz", "eon", "jazz", "brio", "figo", "freestyle", "micra", "spark",
                       "sail", "nano", "indica", "zen", "estilo", "a-star", "pulse",
                       "redi-go", "datsun go", "bmw hatch", "mini hatch", "liva", "bolt", "punto", "fabia", "kuv100"],

  // ── Vans and buses ────────────────────────────────────────────────────────
  TEMPO_TRAVELLER_11: ["traveller", "force traveller", "winger"],
  TEMPO_TRAVELLER_12: ["traveller 12"],
  TEMPO_TRAVELLER_16: ["traveller 16", "urbania", "citiline"],
  MINI_BUS_13:        ["mini bus 13", "minibus 13"],
  MINI_BUS_23:        ["mini bus", "minibus", "school bus", "swaraj mazda", "eicher", "tourister"],

  /* Seat count alone decides these - a 26- and a 32-seater are described the same
     way in a model string, so a keyword could only ever guess between them. Every
     bus on the platform arrived with a seat count and was placed correctly by the
     seat bands; a coach in the catalogue with no count is better left to the count
     it gets at onboarding than pushed onto a tier by its name. */
  BUS_24:             [],
  BUS_26:             [],
  BUS_28:             [],
  BUS_32:             []
};

export const DISPLAY_LABELS = {
  HATCHBACK_5: "Hatchback",
  SEDAN_5_STD: "Sedan",
  SEDAN_5_LUX: "Sedan",
  MINI_SUV_5: "Mini SUV",
  SUV_5: "SUV",
  SUV_7_ECO: "MPV",
  SUV_7_STD: "MPV",
  SUV_7_COMFORT: "MPV",
  SUV_7_LUX: "MPV",
  SUV_7_PREMIUM: "SUV",
  TEMPO_TRAVELLER_11: "Tempo Traveller (11 Seater)",
  TEMPO_TRAVELLER_12: "Tempo Traveller (12 Seater)",
  TEMPO_TRAVELLER_16: "Tempo Traveller (16 Seater)",
  MINI_BUS_13: "Mini Bus",
  MINI_BUS_23: "Bus (20 Seater)",
  BUS_24: "Bus (23-24 Seater)",
  BUS_26: "Bus (26 Seater)",
  BUS_28: "Bus (28 Seater)",
  BUS_32: "Bus (32 Seater)",
};

/**
 * Auto-detect car category from model name and seat count.
 * @param {string} model - Full car model string
 * @param {string|number} [seats] - Seat count
 * @returns {{ key: string, label: string }} - Category key and display label
 */
/* ── Admin-supplied configuration ────────────────────────────────────────────
 *
 * This module is the ONLY category detector. It used to have a twin in
 * frontend/src/utils/carCategory.js, kept in step by a test, because that one could
 * fetch an admin config and this one — which the backend also runs — could not import
 * anything browser-shaped. The twin had drifted into something worse than duplication:
 * only the browser copy read the admin config, so changing a keyword re-categorised a
 * car on screen while the server kept pricing it the old way.
 *
 * The fix is to take the config as input instead of fetching it. Each side loads it
 * the way it can — the browser over HTTP, the server from its config table — and hands
 * it in here. One implementation, one set of rules, either side of the wire.
 *
 * Nothing is required. With no config injected the hardcoded tables below apply, which
 * is exactly the behaviour that existed before any of this.
 */
let _cfg = { keywords: null, modelOverrides: null, shortNames: null, seatOverrides: null };

/* Anyone who needs to redraw when the rules change.
 *
 * The config arrives over the network after the first render and is installed into
 * module state, which React cannot see. Every category and card name already on
 * screen therefore kept the value it was rendered with — so an admin who mapped a
 * model watched the car carry on showing its old tier and concluded the save had not
 * worked. It had; nothing had asked the page to look again. */
const _listeners = new Set();

export function onCategoryConfigChange(fn) {
  _listeners.add(fn);
  return () => _listeners.delete(fn);
}

export function notifyCategoryConfigChanged() {
  for (const fn of _listeners) {
    try { fn(); } catch (err) { console.error("[carCategory] listener failed:", err); }
  }
}

export function setCategoryConfig(cfg) {
  if (!cfg || typeof cfg !== "object") return;
  _cfg = {
    keywords: cfg.keywords && typeof cfg.keywords === "object" ? cfg.keywords : _cfg.keywords,
    modelOverrides: cfg.modelOverrides && typeof cfg.modelOverrides === "object" ? cfg.modelOverrides : _cfg.modelOverrides,
    shortNames: cfg.shortNames && typeof cfg.shortNames === "object" ? cfg.shortNames : _cfg.shortNames,
    seatOverrides: cfg.seatOverrides && typeof cfg.seatOverrides === "object" ? cfg.seatOverrides : _cfg.seatOverrides,
  };
}

export function getCategoryConfig() {
  return _cfg;
}

/* Punctuation is not part of a model's name, so it must not be part of the match.
 *
 * A car is stored as its catalogue label — "Toyota Innova - Crysta 2.4 G (Diesel)" —
 * and the rule an admin writes is a model name, "Toyota Innova Crysta". Compared
 * literally those do not match: the dash sits exactly where the rule expects a space.
 * The shorter "Toyota Innova" DID match, so the least specific rule won and every
 * Crysta on the platform was labelled "Innova".
 *
 * Collapsing every run of non-alphanumerics to one space on both sides fixes it
 * without loosening anything: "toyota innova crysta" is then a plain substring of
 * "toyota innova crysta 2 4 g diesel". Matching stays contiguous, so "3xo" still
 * finds "xuv3xo" and "city" still does not find "cityride" — word-boundary matching
 * would have broken the first of those.
 */
function normaliseForMatch(text) {
  return String(text || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
}

/* Longest pattern wins, always.
 * "innova" and "innova crysta" are both legitimate patterns for different tiers, and
 * whichever is tried first would otherwise decide — so the more specific one has to
 * be chosen deliberately rather than by the order an admin happened to type them in.
 * Length is measured on the normalised form so punctuation cannot inflate a key.
 */
function matchLongestKey(map, haystack) {
  if (!map) return null;
  const hay = normaliseForMatch(haystack);
  let bestKey = null;
  let bestLen = -1;
  for (const key of Object.keys(map)) {
    const k = normaliseForMatch(key);
    if (!k || !hay.includes(k)) continue;
    if (k.length > bestLen) { bestLen = k.length; bestKey = key; }
  }
  return bestKey === null ? null : map[bestKey];
}

/* The tier an admin has mapped this model to, if any.
 *
 * A model string does not reliably say what a vehicle is — "BMW 3 Series" matches no
 * keyword, falls through to the five-seat default and is priced as a MINI_SUV. Keyword
 * tuning cannot fix that in general, so an admin can name the tier for a model outright
 * and every car of that model follows it.
 *
 * This outranks the seat bands as well as the keywords, because it is the one rule a
 * person chose deliberately.
 */
function mappedCategory(m) {
  /* An admin's rule first, then the built-in ones. Same precedence as the keywords:
     what somebody decided beats what shipped, and both beat inference. */
  const own = matchLongestKey(_cfg.modelOverrides, m);
  if (typeof own === "string" && own.trim()) return own.trim();
  const builtin = matchLongestKey(BUILTIN_MODEL_TIERS, m);
  return typeof builtin === "string" && builtin.trim() ? builtin.trim() : null;
}

export function detectCarCategory(model = "", seats) {
  const m = String(model || "").toLowerCase().trim();
  // An admin's seat count for this model wins over a caller that did not supply one.
  // It never overrides a real count from the vehicle's own record.
  const seatNum = parseInt(seats, 10) || parseInt(resolveSeatsFor(m, 0), 10) || 0;

  // An admin's model mapping is a decision, so it comes first — ahead of the seat
  // bands and the keywords, both of which are inference.
  const mapped = mappedCategory(m);
  if (mapped) return { key: mapped, label: categoryLabel(mapped) || DISPLAY_LABELS[mapped] || mapped };
  
  // Strict seat-count override for high-capacity vehicles.
  //
  // Descending, and every band closed, so there is no seat count above 20 without a
  // tier: 20–22, 23–25, 26–27, 28–31, 32+. Before these existed everything from 20 up
  // fell into MINI_BUS_23, which is how a 32-seater came to be priced as a 20-seater.
  if (seatNum >= 9) {
    if (seatNum >= 32) return { key: "BUS_32", label: DISPLAY_LABELS["BUS_32"] };
    if (seatNum >= 28) return { key: "BUS_28", label: DISPLAY_LABELS["BUS_28"] };
    if (seatNum >= 26) return { key: "BUS_26", label: DISPLAY_LABELS["BUS_26"] };
    if (seatNum >= 23) return { key: "BUS_24", label: DISPLAY_LABELS["BUS_24"] };
    if (seatNum >= 20) return { key: "MINI_BUS_23", label: DISPLAY_LABELS["MINI_BUS_23"] };
    if (seatNum >= 16) return { key: "TEMPO_TRAVELLER_16", label: DISPLAY_LABELS["TEMPO_TRAVELLER_16"] };
    if (seatNum >= 13) return { key: "MINI_BUS_13", label: DISPLAY_LABELS["MINI_BUS_13"] };
    if (seatNum >= 12) return { key: "TEMPO_TRAVELLER_12", label: DISPLAY_LABELS["TEMPO_TRAVELLER_12"] };
    return { key: "TEMPO_TRAVELLER_11", label: DISPLAY_LABELS["TEMPO_TRAVELLER_11"] };
  }

  /* Keyword match, most specific tier first.
   *
   * Custom categories lead: an admin who created a tier and gave it words meant those
   * words to win over the built-in guesses. Then the seven-seat tiers, then the
   * five-seat ones, as before.
   *
   * The large-vehicle tiers join the list ONLY when the seat count is unknown. With a
   * count they are already decided above, and consulting names as well would let a
   * five-seater whose name contains "traveller" be priced as a minibus. With no count
   * at all the old list could not reach them — every bus fell past the ten car
   * categories and out the bottom as a SEDAN_5_STD. */
  const order = [
    ...getCustomCategories().map((c) => c.key),
    "SUV_7_PREMIUM", "SUV_7_LUX", "SUV_7_COMFORT", "SUV_7_STD", "SUV_7_ECO",
    "SUV_5", "MINI_SUV_5", "SEDAN_5_LUX", "SEDAN_5_STD", "HATCHBACK_5",
    ...(seatNum === 0
      ? ["BUS_32", "BUS_28", "BUS_26", "BUS_24", "MINI_BUS_23", "MINI_BUS_13",
         "TEMPO_TRAVELLER_16", "TEMPO_TRAVELLER_12", "TEMPO_TRAVELLER_11"]
      : []),
  ];

  // Admin keywords when they have been supplied, the built-in table otherwise. Per
  // category, not all-or-nothing: a config that names three categories leaves the
  // other sixteen on their built-in words rather than blanking them.
  const table = _cfg.keywords || {};
  for (const cat of order) {
    // A category may also require a seat range. "Traveller" cannot tell a 13-seater
    // from a 26-seater on its own; the name says which family, the count says which
    // tier. Skipped when the count is unknown — see seatRuleAllows.
    if (!seatRuleAllows(cat, seatNum)) continue;
    const keywords = Array.isArray(table[cat]) ? table[cat] : (HARDCODED_KEYWORDS[cat] || []);
    for (const kw of keywords) {
      if (m.includes(kw.toLowerCase())) {
        return { key: cat, label: categoryLabel(cat) || DISPLAY_LABELS[cat] || cat };
      }
    }
  }

  // Seat-count fallback (when no keyword matches)
  if (seatNum >= 6)  return { key: "SUV_7_ECO", label: DISPLAY_LABELS["SUV_7_ECO"] }; // Safest economy default for 7-seaters
  if (seatNum === 5 && m.includes("suv")) return { key: "SUV_5", label: DISPLAY_LABELS["SUV_5"] };
  if (seatNum === 5) return { key: "MINI_SUV_5", label: DISPLAY_LABELS["MINI_SUV_5"] };
  if (seatNum <= 4 && seatNum > 0) return { key: "SEDAN_5_STD", label: DISPLAY_LABELS["SEDAN_5_STD"] };

  // Default fallback if we literally have nothing to go on
  return { key: "SEDAN_5_STD", label: DISPLAY_LABELS["SEDAN_5_STD"] };
}

/* The seat count for a model, as an admin has set it.
 *
 * detectSeats reads the model string, which works when the string says so
 * ("Traveller 4020 (20 STR)") and guesses when it does not. Seats decide the tier for
 * anything above eight, so a wrong guess is a mispriced vehicle — that is how a
 * 20-seater came to be stored as a 12-seater. An admin can now state it per model.
 *
 * Falls back to whatever the caller already had, so nothing changes for a model nobody
 * has configured.
 */
export function resolveSeatsFor(model = "", fallback = null) {
  const m = String(model || "").toLowerCase();
  const named = matchLongestKey(_cfg.seatOverrides, m);
  const n = parseInt(named, 10);
  if (Number.isFinite(n) && n > 0) return n;

  /* A count the caller supplied - a real car's own seats - beats a built-in figure
     for its model. The built-in only answers for a catalogue entry nobody has
     onboarded, or a record that never carried a count. */
  if (fallback !== null && fallback !== undefined && parseInt(fallback, 10) > 0) return fallback;

  const builtin = parseInt(matchLongestKey(BUILTIN_MODEL_SEATS, m), 10);
  return Number.isFinite(builtin) && builtin > 0 ? builtin : fallback;
}

/* The name shown on a car card.
 *
 * The grid has room for a couple of words, so "Maruti Suzuki Swift Dzire - VDi
 * (Diesel)" has to become "Dzire". That was done by a heuristic with a hardcoded list
 * of manufacturers to strip, which worked for the makes on the list and produced
 * whatever it produced for the rest — BMW, Audi, Mercedes, Nissan and Jeep were all
 * absent, so a 3 Series came out as "3".
 *
 * An admin can now name it outright per model, and the heuristic stays as the fallback
 * for everything nobody has bothered to name. Longest pattern first, so "swift dzire"
 * can be named differently from "swift".
 */
export function resolveShortName(model = "") {
  const raw = String(model || "").trim();
  if (!raw) return "Vehicle";

  const named = matchLongestKey(_cfg.shortNames, raw.toLowerCase());
  if (typeof named === "string" && named.trim()) return named.trim();

  const cleaned = formatCarName(raw);
  const parts = cleaned.trim().split(/\s+/);
  if (parts.length === 1) return cleaned.replace(/\s*-\s*$/, "");
  const MAKES = ["maruti", "suzuki", "hyundai", "toyota", "honda", "tata", "kia", "mahindra",
    "renault", "mg", "ford", "volkswagen", "skoda", "bmw", "audi", "mercedes", "mercedes-benz",
    "nissan", "jeep", "citroen", "citroën", "volvo", "lexus", "datsun", "isuzu", "force"];
  const filtered = parts.filter((x) => !MAKES.includes(x.toLowerCase()) && !["-", "—", "–"].includes(x));
  const result = filtered.length >= 2 ? filtered.slice(0, 2).join(" ") : (filtered.join(" ") || cleaned);
  return result.replace(/\s*-\s*$/, "").trim();
}

/**
 * Map a legacy category key to the new 13-category schema.
 * Used during migration and for DB records that still carry old keys.
 */
export function mapLegacyCategory(oldKey = "") {
  const map = {
    HATCHBACK:       "HATCHBACK_5",
    SEDAN:           "SEDAN_5_STD",
    MINI_SUV:        "MINI_SUV_5",
    SUV:             "SUV_7_STD",
    MPV_LUX:         "SUV_7_COMFORT",
    TEMPO_TRAVELLER: "TEMPO_TRAVELLER_11",
    MINI_BUS:        "MINI_BUS_13",

    // Older aliases
    MINI:            "HATCHBACK_5",
    SUV_5:           "MINI_SUV_5",
    SUV_7:           "SUV_7_STD",
    LUXURY:          "SUV_7_COMFORT",
    MINIVAN:         "SUV_7_STD",
    MPV:             "SUV_7_STD",
  };
  return map[oldKey] || oldKey || "SEDAN_5_STD";
}

/**
 * Format car display name: extract the short popular model name only.
 */
const KNOWN_MODELS = [
  "verna","creta","i10","i20","aura","venue","exter","alcazar","tucson","kona",
  "dzire","swift","baleno","brezza","ertiga","xl6","ignis","alto","wagonr","wagon r","ciaz","s-presso","fronx","jimny","invicto","grand vitara",
  "innova","fortuner","glanza","yaris","urban cruiser","hyryder","taisor","camry","vellfire",
  "nexon","tiago","tigor","harrier","safari","punch","altroz","nano",
  "seltos","sonet","carens","carnival","ev6",
  "xuv700","xuv500","xuv400","xuv300","thar","scorpio","bolero","marazzo",
  "hector","comet","astor","gloster",
  "magnite","kicks","terrano","x-trail",
  "rapid","slavia","kushaq","kodiaq","superb","octavia",
  "city","amaze","wrv","elevate",
  "polo","virtus","taigun","tiguan",
  "c3","ec3","c3 aircross",
  "kwid","triber","kiger",
  "xc40","s60","xc60","xc90",
  "a-class","c-class","e-class","gle","gla","glc","glb",
  "3 series","5 series","x1","x3","x5",
  "q2","q3","q5","a4","a6",
  "compass","meridian","wrangler","grand cherokee",
  "crysta","hycross","rumion","hilux",
  "eeco","omni",
  "traveller","winger",
];

export function formatCarName(name = "") {
  const lower = name.toLowerCase().trim();
  for (const m of KNOWN_MODELS) {
    if (lower.includes(m)) {
      return m.split(/[\s-]+/).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
    }
  }
  const words = name.trim().split(/\s+/);
  if (words.length >= 2) return words[1];
  return words[0] || "Vehicle";
}

/**
 * Auto-detect seat count from model name
 */
/**
 * Pull an explicit seat count out of a model string.
 *
 * MUST STAY IN STEP with frontend/src/utils/carCategory.js. The duplication is
 * deliberate — that copy imports apiRequest for dynamic keyword loading, and this one
 * is the zero-browser-dependency version the backend consumes through the
 * `sramika-pricing` file: dependency. carCategory.test.js asserts both agree.
 *
 * The keyword fallback below matched "traveller" and returned a hardcoded "12", which
 * made "traveller 16" unreachable and mis-sized every Tempo Traveller. Seats decide the
 * pricing tier, so a wrong default is a wrong price.
 *
 * Only counts in a seating context are accepted. A bare number is ignored, because model
 * names are full of numbers that are not seats — "4020" is a variant code, "xuv700" a
 * model, "2022" a year.
 */
/* Exported so a screen can ask the narrow question - does this NAME state a seat
   count - without taking detectSeats's guesses. detectSeats falls back to 5 for
   anything it cannot place, which would price a coach as a five-seater. */
export function seatsFromModelString(m) {
  const patterns = [
    /(\d{1,2})\s*(?:str|seater|seats|seat)\b/,      // "20 STR", "16 seater", "12 seats"
    /\bseat(?:er|ing)?\s*[:-]?\s*(\d{1,2})\b/,      // "seater: 20", "seating 16"
    /\btempo\s+traveller\s+(\d{1,2})\b/,            // "Tempo Traveller 26"
    /\btraveller\s+(\d{1,2})\b/,                    // "Traveller 16"
  ];
  for (const re of patterns) {
    const hit = m.match(re);
    if (hit) {
      const n = parseInt(hit[1], 10);
      // Plausible passenger-vehicle range. Anything outside it is not a seat count.
      if (n >= 2 && n <= 60) return String(n);
    }
  }
  return null;
}

export function detectSeats(model = "") {
  const m = model.toLowerCase().trim();

  // An explicit count in the name always wins over a keyword guess.
  const explicit = seatsFromModelString(m);
  if (explicit) return explicit;
  
  const mbKw = [...HARDCODED_KEYWORDS.MINI_BUS_13, ...HARDCODED_KEYWORDS.MINI_BUS_23];
  for (const name of mbKw) {
    if (m.includes(name)) return "23";
  }

  const ttKw = [
    ...HARDCODED_KEYWORDS.TEMPO_TRAVELLER_11, 
    ...HARDCODED_KEYWORDS.TEMPO_TRAVELLER_12, 
    ...HARDCODED_KEYWORDS.TEMPO_TRAVELLER_16
  ];
  for (const name of ttKw) {
    if (m.includes(name)) return "12";
  }

  const KNOWN_7 = [
    "scorpio", "scorpio n", "bolero", "bolero plus", "bolero power+",
    "xuv500", "xuv700", "safari", "fortuner", "endeavour",
    "hector plus", "alcazar", "meridian", "innova", "crysta", "hycross",
    "ertiga", "xl6", "carens",
  ];
  for (const name of KNOWN_7) {
    if (m.includes(name)) return "7";
  }
  if (m.includes("innova crysta")) return "8";
  return "5";
}

// ── Category badge colors ──────────────────────────────────────────────────
export const CATEGORY_COLORS = {
  HATCHBACK_5:        { bg: "#E8F5E9", text: "#2E7D32",  label: "Hatchback"        },
  SEDAN_5_STD:        { bg: "#E3F2FD", text: "#1565C0",  label: "Sedan Standard"   },
  SEDAN_5_LUX:        { bg: "#E3F2FD", text: "#0D47A1",  label: "Sedan Luxury"     },
  MINI_SUV_5:         { bg: "#FFF3E0", text: "#E65100",  label: "Mini SUV"         },
  SUV_5:              { bg: "#FBE9E7", text: "#D84315",  label: "SUV 5 Seater"     },
  SUV_7_ECO:          { bg: "#FCE4EC", text: "#AD1457",  label: "7 Seater Economy" },
  SUV_7_STD:          { bg: "#FCE4EC", text: "#C62828",  label: "7 Seater Standard"},
  SUV_7_COMFORT:      { bg: "#F3E5F5", text: "#6A1B9A",  label: "7 Seater Comfort" },
  SUV_7_LUX:          { bg: "#EDE7F6", text: "#4527A0",  label: "7 Seater Luxury"  },
  SUV_7_PREMIUM:      { bg: "#311B92", text: "#FFFFFF",  label: "7 Seater Premium" },
  TEMPO_TRAVELLER_11: { bg: "#EDE7F6", text: "#4527A0",  label: "Tempo Traveller (11 Seater)"  },
  TEMPO_TRAVELLER_12: { bg: "#EDE7F6", text: "#4527A0",  label: "Tempo Traveller (12 Seater)"  },
  TEMPO_TRAVELLER_16: { bg: "#EDE7F6", text: "#4527A0",  label: "Tempo Traveller (16 Seater)"  },
  MINI_BUS_13:        { bg: "#E8EAF6", text: "#1A237E",  label: "Mini Bus"         },
  MINI_BUS_23:        { bg: "#E8EAF6", text: "#1A237E",  label: "Bus (20 Seater)"  },
  BUS_24:             { bg: "#E8EAF6", text: "#1A237E",  label: "Bus (23-24 Seater)" },
  BUS_26:             { bg: "#E1F5FE", text: "#01579B",  label: "Bus (26 Seater)"  },
  BUS_28:             { bg: "#E0F2F1", text: "#004D40",  label: "Bus (28 Seater)"  },
  BUS_32:             { bg: "#1A237E", text: "#FFFFFF",  label: "Bus (32 Seater)"  },

  // Legacy aliases
  HATCHBACK:       { bg: "#E8F5E9", text: "#2E7D32",  label: "Hatchback"        },
  SEDAN:           { bg: "#E3F2FD", text: "#1565C0",  label: "Sedan"            },
  MINI_SUV:        { bg: "#FFF3E0", text: "#E65100",  label: "Mini SUV"         },
  SUV:             { bg: "#FCE4EC", text: "#C62828",  label: "SUV"              },
  MPV_LUX:         { bg: "#F3E5F5", text: "#6A1B9A",  label: "MPV Luxury"       },
  TEMPO_TRAVELLER: { bg: "#EDE7F6", text: "#4527A0",  label: "Tempo Traveller"  },
  MINI_BUS:        { bg: "#E8EAF6", text: "#1A237E",  label: "Mini Bus"         },
};
